<template>
  <div class="mx-auto p-4 bg-warm-gray-900 min-h-screen">
    <div class="mx-auto">
      <h1 class="text-2xl font-bold mb-6 text-gray-100">Transactions</h1>

      <!-- Filters -->
      <div class="flex items-center justify-between mb-6">
        <!-- Left Side: Month Navigation & Endorser Filter -->
        <div class="flex items-center gap-4">
          <!-- Month Navigation -->
          <div class="flex items-center gap-4">
            <button
              @click="previousMonth"
              :disabled="loading"
              class="px-3 py-2 text-gray-300 bg-warm-gray-800 hover:bg-warm-gray-700 border border-warm-gray-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-warm-gray-800"
            >
              &larr;
            </button>
            <div class="text-lg font-semibold text-gray-100 min-w-[120px] text-center">
              {{ currentMonthDisplay }}
            </div>
            <button
              @click="nextMonth"
              :disabled="loading"
              class="px-3 py-2 text-gray-300 bg-warm-gray-800 hover:bg-warm-gray-700 border border-warm-gray-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-warm-gray-800"
            >
              &rarr;
            </button>
          </div>
          
          <!-- Endorser Code Filter -->
          <div class="flex items-center gap-2">
            <label class="text-sm font-medium text-gray-300 whitespace-nowrap">Endorser:</label>
            <select
              v-model="selectedEndorserCode"
              @change="applyEndorserFilter"
              :disabled="loading || availableEndorserCodes.length === 0"
              class="px-3 py-2 bg-warm-gray-900 border border-warm-gray-700 rounded text-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed min-w-[150px] no-arrow"
            >
              <option value="">All</option>
              <option
                v-for="code in availableEndorserCodes"
                :key="code"
                :value="code"
              >
                {{ code }}
              </option>
            </select>
          </div>
          
          <!-- Type Filter -->
          <div class="flex items-center gap-2">
            <label class="text-sm font-medium text-gray-300 whitespace-nowrap">Type:</label>
            <select
              v-model="selectedType"
              :disabled="loading"
              class="px-3 py-2 bg-warm-gray-900 border border-warm-gray-700 rounded text-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed min-w-[120px] no-arrow"
            >
              <option value="">All</option>
              <option value="bundled">Bundle</option>
              <option value="individual">Individual</option>
            </select>
          </div>
          
          <!-- School Filter -->
          <div class="flex items-center gap-2">
            <label class="text-sm font-medium text-gray-300 whitespace-nowrap">School:</label>
            <select
              v-model="selectedSchool"
              :disabled="loading || availableSchools.length === 0"
              class="px-3 py-2 bg-warm-gray-900 border border-warm-gray-700 rounded text-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed min-w-[180px] no-arrow"
            >
              <option value="">All</option>
              <option
                v-for="school in availableSchools"
                :key="school.id"
                :value="school.id"
              >
                {{ school.name }}
              </option>
            </select>
          </div>
        </div>
        
        <!-- Right Side: Total Amount -->
        <div class="text-lg font-semibold text-gray-100">
          Total: ₱{{ formatCurrency(totalAmount) }}
        </div>
      </div>

    <!-- Transactions Table -->
    <div v-if="transactions.length > 0" class="overflow-x-auto">
      <table class="w-full text-sm text-left">
        <thead class="text-xs text-gray-400 uppercase border-b border-warm-gray-700">
          <tr>
            <th class="px-4 py-3 w-12">
              <button
                @click="toggleAllExcluded"
                :disabled="loading"
                class="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                :title="allExcluded ? 'Show all in total' : 'Hide all from total'"
              >
                <EyeSlashIcon v-if="allExcluded" class="w-5 h-5" />
                <EyeIcon v-else class="w-5 h-5" />
              </button>
            </th>
            <th class="px-4 py-3">User</th>
            <th class="px-4 py-3">Type</th>
            <th class="px-4 py-3">Subscriptions</th>
            <th class="px-4 py-3">Coupon</th>
            <th class="px-4 py-3">
              <div class="flex items-center gap-2">
                <span>Date</span>
                <button
                  @click="toggleSortDirection"
                  :disabled="loading"
                  class="w-5 h-5 flex items-center justify-center text-xs text-gray-300 hover:text-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  :title="sortDirection === 'desc' ? 'Newest First (Click for Oldest)' : 'Oldest First (Click for Newest)'"
                >
                  {{ sortDirection === 'desc' ? '▲' : '▼' }}
                </button>
              </div>
            </th>
            <th class="px-4 py-3 text-right">Amount</th>
            <th class="px-4 py-3"></th>
          </tr>
        </thead>
        <tbody class="text-gray-300">
          <tr
            v-for="transaction in filteredTransactions"
            :key="transaction.id"
            :class="[
              'border-b border-warm-gray-800 transition-all',
              isExcluded(transaction.id)
                ? 'opacity-25 grayscale hover:opacity-30'
                : 'hover:bg-gray-800/50'
            ]"
          >
            <td class="px-4 py-3">
              <button
                @click="toggleExcluded(transaction.id)"
                :disabled="loading"
                class="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                :title="isExcluded(transaction.id) ? 'Show in total' : 'Hide from total'"
              >
                <EyeSlashIcon v-if="isExcluded(transaction.id)" class="w-5 h-5" />
                <EyeIcon v-else class="w-5 h-5" />
              </button>
            </td>
            <td class="px-4 py-3">
              <div>
                <div class="font-medium text-gray-100">
                  {{ transaction.data.user_firstname || '' }} {{ transaction.data.user_lastname || '' }}
                </div>
                <div class="text-xs text-gray-500 mt-0.5">
                  {{ transaction.data.user_email || 'N/A' }}
                </div>
                <div v-if="transaction.schoolName" class="text-xs text-gray-500 mt-0.5">
                  {{ transaction.schoolName }}
                </div>
              </div>
            </td>
            <td class="px-4 py-3 text-gray-400">
              <div>
                <div>{{ transaction.data.purchase_type === 'bundled' ? transaction.data.bundle_name || 'Bundle' : 'Individual' }}</div>
                <div v-if="transaction.data.gcash_ref_code" class="text-xs text-gray-500 mt-1">
                  Ref Code: {{ transaction.data.gcash_ref_code }}
                </div>
                <div v-else-if="transaction.data.proof_image_url" class="text-xs text-gray-500 mt-1">
                    Proof: <a
                    :href="transaction.data.proof_image_url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-blue-400 hover:text-blue-300 underline"
                  >
                    View Image
                  </a>
                </div>
              </div>
            </td>
            <td class="px-4 py-3">
              <div v-if="transaction.data.subscriptions && transaction.data.subscriptions.length > 0" class="space-y-1">
                <div
                  v-for="(sub, index) in transaction.data.subscriptions"
                  :key="index"
                  class="text-xs"
                >
                  {{ sub.reviewer_name }}
                  <span class="text-gray-500">({{ sub.duration_in_words }})</span>
                </div>
              </div>
              <span v-else class="text-gray-500">—</span>
            </td>
            <td class="px-4 py-3">
              <div v-if="transaction.data.coupon" class="space-y-1">
                <div class="text-xs">
                  <span class="text-gray-400">Code:</span>
                  <span class="text-gray-200 font-medium ml-1">{{ transaction.data.coupon.coupon_code }}</span>
                </div>
                <div v-if="transaction.endorserCode" class="text-xs">
                  <span class="text-gray-400">Endorser:</span>
                  <span class="text-gray-200 font-medium ml-1">{{ transaction.endorserCode }}</span>
                </div>
              </div>
              <span v-else class="text-gray-500">—</span>
            </td>
            <td class="px-4 py-3 text-gray-400">
              <div>
                <div>{{ formatDate(transaction.data.purchase_date) }}</div>
                <div v-if="transaction.data.purchasedByAdminEmail" class="text-xs text-blue-400 mt-1 underline">
                  By Admin: {{ transaction.data.purchasedByAdminEmail }}
                </div>
              </div>
            </td>
            <td class="px-4 py-3 text-right font-medium text-gray-100">
              ₱{{ formatCurrency(transaction.data.total_amount) }}
            </td>
            <td class="px-4 py-3">
              <div class="flex items-center gap-2">
                <button
                  @click="openNotesModal(transaction)"
                  :disabled="loading"
                  class="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  :title="transaction.data.notes ? 'View/Edit notes' : 'Add notes'"
                >
                  <DocumentTextIcon class="w-5 h-5" :class="transaction.data.notes ? 'text-blue-400' : ''" />
                </button>
                <button
                  @click="openRawModal(transaction)"
                  :disabled="loading"
                  class="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="View raw data"
                >
                  <CodeBracketIcon class="w-5 h-5" />
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-else-if="!loading" class="text-center py-12 text-gray-500">
      <span v-if="selectedEndorserCode || selectedType || selectedSchool">
        No transactions found
        <span v-if="selectedEndorserCode"> for endorser code: {{ selectedEndorserCode }}</span>
        <span v-if="selectedType"> with type: {{ selectedType === 'bundled' ? 'Bundle' : 'Individual' }}</span>
        <span v-if="selectedSchool"> from school: {{ getSchoolName(selectedSchool) }}</span>.
      </span>
      <span v-else>No transactions found.</span>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center items-center py-12">
      <svg
        class="animate-spin h-8 w-8 text-gray-400"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          class="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="4"
        ></circle>
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        ></path>
      </svg>
    </div>

    <!-- Load More Button -->
    <div v-if="!loading && transactions.length > 0" class="text-center mt-6">
      <div class="my-3 text-sm text-gray-500">
        Showing {{ filteredTransactions.length }} of {{ transactions.length }} transaction{{ transactions.length !== 1 ? 's' : '' }}
        <span v-if="selectedEndorserCode || selectedType || selectedSchool" class="text-gray-400">
          (filtered by:
          <span v-if="selectedEndorserCode"> {{ selectedEndorserCode }}</span>
          <span v-if="selectedEndorserCode && (selectedType || selectedSchool)">, </span>
          <span v-if="selectedType">{{ selectedType === 'bundled' ? 'Bundle' : 'Individual' }}</span>
          <span v-if="selectedType && selectedSchool">, </span>
          <span v-if="selectedSchool">{{ getSchoolName(selectedSchool) }}</span>)
        </span>
      </div>
      <button
        v-if="!isLastPage"
        @click="loadMoreTransactions"
        :disabled="loading || loadingMore"
        type="button"
        class="mx-auto inline-flex items-center rounded px-4 py-2 text-sm font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 border border-warm-gray-700 hover:border-warm-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <svg
          v-if="loadingMore"
          class="animate-spin -ml-1 mr-2 h-4 w-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          ></circle>
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
        {{ loadingMore ? 'Loading...' : 'Load More' }}
        </button>
      </div>
    </div>

    <!-- Notes Modal -->
    <div
      v-if="showNotesModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      @click.self="!savingNotes && closeNotesModal()"
    >
      <div class="bg-warm-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col">
        <!-- Modal Header -->
        <div class="flex items-center justify-between p-4 border-b border-warm-gray-700">
          <h2 class="text-lg font-semibold text-gray-100">Transaction Notes</h2>
          <button
            @click="closeNotesModal"
            :disabled="savingNotes"
            class="text-gray-400 hover:text-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <!-- Modal Body -->
        <div class="p-4 overflow-auto flex-1">
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-300 mb-2">Notes</label>
              <textarea
                v-model="notesText"
                :disabled="savingNotes"
                rows="8"
                class="w-full px-3 py-2 bg-warm-gray-900 border border-warm-gray-700 rounded text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="Add notes about this transaction..."
              ></textarea>
            </div>
            <div v-if="currentTransaction?.data?.notes" class="text-xs text-gray-500 space-y-1">
              <div v-if="currentTransaction.data.notes.lastUpdated">
                <span class="text-gray-400">Last updated:</span> {{ formatDate(currentTransaction.data.notes.lastUpdated) }}
              </div>
              <div v-if="currentTransaction.data.notes.lastUpdateBy">
                <span class="text-gray-400">Last updated by:</span> {{ currentTransaction.data.notes.lastUpdateBy }}
              </div>
            </div>
          </div>
        </div>
        
        <!-- Modal Footer -->
        <div class="flex items-center justify-end gap-2 p-4 border-t border-warm-gray-700">
          <button
            @click="closeNotesModal"
            :disabled="savingNotes"
            class="px-4 py-2 text-sm font-medium text-gray-300 bg-warm-gray-700 hover:bg-warm-gray-600 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            @click="saveNotes"
            :disabled="savingNotes || loading"
            class="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span v-if="savingNotes">Saving...</span>
            <span v-else>Save</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Raw Data Modal -->
    <div
      v-if="showRawModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      @click.self="closeRawModal"
    >
      <div class="bg-warm-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col">
        <!-- Modal Header -->
        <div class="flex items-center justify-between p-4 border-b border-warm-gray-700">
          <h2 class="text-lg font-semibold text-gray-100">Raw Transaction Data</h2>
          <button
            @click="closeRawModal"
            class="text-gray-400 hover:text-gray-200 transition-colors"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <!-- Modal Body -->
        <div class="p-4 overflow-auto flex-1">
          <pre class="text-xs text-gray-300 bg-warm-gray-900 p-4 rounded border border-warm-gray-700 overflow-auto">{{ rawTransactionData }}</pre>
        </div>
        
        <!-- Modal Footer -->
        <div class="flex items-center justify-end p-4 border-t border-warm-gray-700">
          <button
            @click="closeRawModal"
            class="px-4 py-2 text-sm font-medium text-gray-300 bg-warm-gray-700 hover:bg-warm-gray-600 rounded transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue'
import { db, auth } from '@/firebase/config.js'
import { query, collection, orderBy, startAfter, limit, getDocs, where, getDoc, doc, onSnapshot, Timestamp, updateDoc, deleteField } from 'firebase/firestore'
import { EyeIcon, EyeSlashIcon, CodeBracketIcon, DocumentTextIcon } from '@heroicons/vue/24/outline'

// Configuration: Adjustable page size
const PAGE_SIZE = 100

// Reactive state
const transactions = ref([])
const lastDocument = ref(null)
const isLastPage = ref(false)
const loading = ref(false)
const loadingMore = ref(false)

// Snapshot listener
let unsubscribeSnapshot = null

// Month filter state
const currentMonth = ref(new Date().getMonth())
const currentYear = ref(new Date().getFullYear())

// Sort direction state (desc = newest first, asc = oldest first)
const sortDirection = ref('desc')

// Endorser code filter state
const selectedEndorserCode = ref('')

// Type filter state
const selectedType = ref('')

// School filter state
const selectedSchool = ref('')

// Raw data modal state
const showRawModal = ref(false)
const rawTransactionData = ref('')

// Notes modal state
const showNotesModal = ref(false)
const notesText = ref('')
const currentTransaction = ref(null)
const savingNotes = ref(false)

// Schools cache (fetch once, reuse)
const schoolsCache = ref(new Map()) // Map<schoolId, schoolName>
const schoolsLoaded = ref(false)

/**
 * Get all unique endorser codes from transactions
 */
const availableEndorserCodes = computed(() => {
  const codes = new Set()
  transactions.value.forEach(transaction => {
    if (transaction.endorserCode) {
      codes.add(transaction.endorserCode)
    }
  })
  return Array.from(codes).sort()
})

/**
 * Get all unique schools from transactions
 */
const availableSchools = computed(() => {
  const schoolMap = new Map()
  transactions.value.forEach(transaction => {
    if (transaction.schoolId && transaction.schoolName) {
      if (!schoolMap.has(transaction.schoolId)) {
        schoolMap.set(transaction.schoolId, {
          id: transaction.schoolId,
          name: transaction.schoolName
        })
      }
    }
  })
  return Array.from(schoolMap.values()).sort((a, b) => a.name.localeCompare(b.name))
})

/**
 * Get school name by ID
 * @param {string} schoolId - School ID
 * @returns {string} School name or 'Unknown'
 */
const getSchoolName = (schoolId) => {
  if (!schoolId) return 'Unknown'
  return schoolsCache.value.get(schoolId) || 'Unknown'
}

/**
 * Filtered transactions based on selected endorser code, type, and school
 */
const filteredTransactions = computed(() => {
  let filtered = transactions.value
  
  // Filter by endorser code
  if (selectedEndorserCode.value) {
    filtered = filtered.filter(transaction => 
      transaction.endorserCode === selectedEndorserCode.value
    )
  }
  
  // Filter by type
  if (selectedType.value) {
    if (selectedType.value === 'bundled') {
      filtered = filtered.filter(transaction => 
        transaction.data.purchase_type === 'bundled'
      )
    } else if (selectedType.value === 'individual') {
      filtered = filtered.filter(transaction => 
        transaction.data.purchase_type !== 'bundled'
      )
    }
  }
  
  // Filter by school
  if (selectedSchool.value) {
    filtered = filtered.filter(transaction => 
      transaction.schoolId === selectedSchool.value
    )
  }
  
  return filtered
})

/**
 * Apply endorser code filter
 */
const applyEndorserFilter = () => {
  // Filtering is handled by computed property, no need to reload
  // This function is here for potential future use (e.g., logging)
}

// Excluded transactions (checked = excluded from total)
const excludedTransactionIds = ref(new Set())

/**
 * Check if a transaction is excluded
 * @param {string} transactionId - Transaction ID
 * @returns {boolean}
 */
const isExcluded = (transactionId) => {
  return excludedTransactionIds.value.has(transactionId)
}

/**
 * Toggle exclusion for a single transaction
 * @param {string} transactionId - Transaction ID
 */
const toggleExcluded = (transactionId) => {
  if (loading.value) {
    return
  }
  
  if (excludedTransactionIds.value.has(transactionId)) {
    excludedTransactionIds.value.delete(transactionId)
  } else {
    excludedTransactionIds.value.add(transactionId)
  }
}

/**
 * Toggle exclusion for all transactions (based on filtered transactions)
 */
const toggleAllExcluded = () => {
  if (loading.value) {
    return
  }
  
  const shouldExcludeAll = !allExcluded.value
  if (shouldExcludeAll) {
    // Exclude all filtered transactions
    filteredTransactions.value.forEach(transaction => {
      excludedTransactionIds.value.add(transaction.id)
    })
  } else {
    // Include all filtered transactions
    filteredTransactions.value.forEach(transaction => {
      excludedTransactionIds.value.delete(transaction.id)
    })
  }
}

/**
 * Check if all transactions are excluded (based on filtered transactions)
 */
const allExcluded = computed(() => {
  if (filteredTransactions.value.length === 0) {
    return false
  }
  return filteredTransactions.value.every(transaction => 
    excludedTransactionIds.value.has(transaction.id)
  )
})

/**
 * Computed total amount for all transactions in the current view (excluding checked items)
 * Note: This uses filteredTransactions to respect the endorser code filter
 */
const totalAmount = computed(() => {
  return filteredTransactions.value.reduce((sum, transaction) => {
    // Skip if transaction is excluded
    if (excludedTransactionIds.value.has(transaction.id)) {
      return sum
    }
    const amount = transaction.data.total_amount || 0
    return sum + amount
  }, 0)
})

/**
 * Get month display string (e.g., "Nov 2024")
 */
const currentMonthDisplay = computed(() => {
  const date = new Date(currentYear.value, currentMonth.value, 1)
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
})

/**
 * Get start and end dates for the current month
 * @returns {{startDate: Date, endDate: Date}}
 */
const getMonthDateRange = () => {
  const startDate = new Date(currentYear.value, currentMonth.value, 1)
  startDate.setHours(0, 0, 0, 0)
  
  const endDate = new Date(currentYear.value, currentMonth.value + 1, 0)
  endDate.setHours(23, 59, 59, 999)
  
  return { startDate, endDate }
}

/**
 * Navigate to previous month
 */
const previousMonth = () => {
  // Prevent navigation if already loading
  if (loading.value) {
    return
  }
  
  if (currentMonth.value === 0) {
    currentMonth.value = 11
    currentYear.value--
  } else {
    currentMonth.value--
  }
  loadInitialTransactions()
}

/**
 * Navigate to next month
 */
const nextMonth = () => {
  // Prevent navigation if already loading
  if (loading.value) {
    return
  }
  
  if (currentMonth.value === 11) {
    currentMonth.value = 0
    currentYear.value++
  } else {
    currentMonth.value++
  }
  loadInitialTransactions()
}

/**
 * Toggle sort direction and reload transactions
 */
const toggleSortDirection = () => {
  // Prevent change if already loading
  if (loading.value) {
    return
  }
  
  // Toggle between 'desc' (newest first) and 'asc' (oldest first)
  sortDirection.value = sortDirection.value === 'desc' ? 'asc' : 'desc'
  loadInitialTransactions()
}

/**
 * Build Firestore query - only fetch confirmed transactions for the selected month
 * @param {Object|null} lastDoc - Last document for pagination
 * @returns {Query} Firestore query
 */
const buildQuery = (lastDoc = null) => {
  const transactionsRef = collection(db, 'transactions')
  const { startDate, endDate } = getMonthDateRange()
  
  // Convert to Firestore Timestamps
  const startTimestamp = Timestamp.fromDate(startDate)
  const endTimestamp = Timestamp.fromDate(endDate)
  
  // Build query constraints - confirmed transactions within the selected month
  const constraints = [
    where('status', '==', 'confirmed'),
    where('purchase_date', '>=', startTimestamp),
    where('purchase_date', '<=', endTimestamp),
    orderBy('purchase_date', sortDirection.value)
  ]
  
  // Add pagination if loading more
  if (lastDoc) {
    constraints.push(startAfter(lastDoc))
  }
  
  // Add limit
  constraints.push(limit(PAGE_SIZE))
  
  return query(transactionsRef, ...constraints)
}

/**
 * Fetch endorser code for a single transaction
 * @param {Object} transactionData - Transaction data
 * @returns {Promise<string|null>} Endorser code or null
 */
const fetchEndorserCode = async (transactionData) => {
  if (!transactionData.coupon || !transactionData.coupon.coupon_code) {
    return null
  }
  
  try {
    const couponQuery = query(
      collection(db, 'coupons'),
      where('code', '==', transactionData.coupon.coupon_code),
      limit(1)
    )
    const couponSnapshot = await getDocs(couponQuery)
    if (!couponSnapshot.empty) {
      const couponData = couponSnapshot.docs[0].data()
      return couponData.assignedToEndorserCode || null
    }
  } catch (error) {
    console.warn(`Failed to fetch endorser code for coupon ${transactionData.coupon.coupon_code}:`, error)
  }
  
  return null
}

/**
 * Fetch endorser codes for transactions
 * @param {Array} transactionDocs - Array of transaction document snapshots
 * @returns {Promise<Array>} Transactions with endorser codes
 */
const fetchEndorserCodes = async (transactionDocs) => {
  return Promise.all(
    transactionDocs.map(async (docSnapshot) => {
      const transactionData = docSnapshot.data()
      const endorserCode = await fetchEndorserCode(transactionData)
      
      return {
        id: docSnapshot.id,
        data: transactionData,
        endorserCode
      }
    })
  )
}

/**
 * Fetch all schools from Firestore and cache them
 * This is called once on mount
 */
const fetchAllSchools = async () => {
  if (schoolsLoaded.value) {
    return // Already loaded
  }
  
  try {
    const schoolsSnapshot = await getDocs(collection(db, 'schools'))
    const schoolsMap = new Map()
    
    schoolsSnapshot.forEach((doc) => {
      const schoolData = doc.data()
      schoolsMap.set(doc.id, schoolData.name || 'Unknown School')
    })
    
    schoolsCache.value = schoolsMap
    schoolsLoaded.value = true
  } catch (error) {
    console.error('Error fetching schools:', error)
  }
}

/**
 * Fetch user school ID for a single transaction
 * @param {string} userEmail - User email
 * @returns {Promise<string|null>} School ID or null
 */
const fetchUserSchoolId = async (userEmail) => {
  if (!userEmail) {
    return null
  }
  
  try {
    const userQuery = query(
      collection(db, 'users'),
      where('email', '==', userEmail),
      limit(1)
    )
    const userSnapshot = await getDocs(userQuery)
    
    if (!userSnapshot.empty) {
      const userData = userSnapshot.docs[0].data()
      return userData.school || null
    }
  } catch (error) {
    console.warn(`Failed to fetch user school for email ${userEmail}:`, error)
  }
  
  return null
}

/**
 * Fetch user schools for transactions (with caching to avoid duplicate queries)
 * @param {Array} transactionDocs - Array of transaction document snapshots
 * @returns {Promise<Array>} Transactions with school names
 */
const fetchUserSchools = async (transactionDocs) => {
  // Extract unique emails
  const uniqueEmails = [...new Set(
    transactionDocs
      .map(doc => doc.data().user_email)
      .filter(Boolean)
  )]
  
  // Cache for user email -> school ID mapping
  const userSchoolCache = new Map()
  
  // Batch fetch users (with caching to avoid duplicate queries for same email)
  await Promise.all(
    uniqueEmails.map(async (email) => {
      if (!userSchoolCache.has(email)) {
        const schoolId = await fetchUserSchoolId(email)
        userSchoolCache.set(email, schoolId)
      }
    })
  )
  
  // Map transactions with school names
  return transactionDocs.map((docSnapshot) => {
    const transactionData = docSnapshot.data()
    const schoolId = userSchoolCache.get(transactionData.user_email)
    const schoolName = schoolId ? (schoolsCache.value.get(schoolId) || 'Unknown School') : null
    
    return {
      id: docSnapshot.id,
      data: transactionData,
      schoolId,
      schoolName
    }
  })
}

/**
 * Fetch endorser codes and schools for transactions
 * @param {Array} transactionDocs - Array of transaction document snapshots
 * @returns {Promise<Array>} Transactions with endorser codes and school names
 */
const fetchEndorserCodesAndSchools = async (transactionDocs) => {
  // Fetch both in parallel
  const [transactionsWithEndorsers, transactionsWithSchools] = await Promise.all([
    fetchEndorserCodes(transactionDocs),
    fetchUserSchools(transactionDocs)
  ])
  
  // Merge the results
  return transactionsWithEndorsers.map((transaction) => {
    const schoolData = transactionsWithSchools.find(t => t.id === transaction.id)
    return {
      ...transaction,
      schoolId: schoolData?.schoolId || null,
      schoolName: schoolData?.schoolName || null
    }
  })
}

/**
 * Fetch transactions from Firestore
 * @param {Object|null} lastDoc - Last document for pagination (null for initial load)
 * @returns {Promise<{transactions: Array, lastDoc: Object|null, isLastPage: boolean}>}
 */
const fetchTransactions = async (lastDoc = null) => {
  try {
    const q = buildQuery(lastDoc)
    const querySnapshot = await getDocs(q)

    // Map documents to transaction objects and fetch endorser codes + schools
    const fetchedTransactions = await fetchEndorserCodesAndSchools(querySnapshot.docs)

    // Determine if this is the last page
    const isLast = querySnapshot.size < PAGE_SIZE

    // Get the last document for pagination
    const newLastDoc = querySnapshot.docs.length > 0 
      ? querySnapshot.docs[querySnapshot.docs.length - 1] 
      : null

    return {
      transactions: fetchedTransactions,
      lastDoc: newLastDoc,
      isLastPage: isLast
    }
  } catch (error) {
    console.error('Error fetching transactions:', error)
    throw error
  }
}

/**
 * Set up real-time snapshot listener for transactions
 */
const setupSnapshotListener = () => {
  // Clean up existing listener if any
  if (unsubscribeSnapshot) {
    unsubscribeSnapshot()
    unsubscribeSnapshot = null
  }
  
  // Reset pagination
  lastDocument.value = null
  isLastPage.value = false
  
  // Clear excluded transactions when reloading
  excludedTransactionIds.value.clear()
  
  loading.value = true
  
  try {
    const q = buildQuery(null)
    
    unsubscribeSnapshot = onSnapshot(
      q,
      async (querySnapshot) => {
        try {
          // Map documents to transaction objects and fetch endorser codes + schools
          const fetchedTransactions = await fetchEndorserCodesAndSchools(querySnapshot.docs)
          
          transactions.value = fetchedTransactions
          
          // Update pagination info
          if (querySnapshot.docs.length > 0) {
            lastDocument.value = querySnapshot.docs[querySnapshot.docs.length - 1]
            isLastPage.value = querySnapshot.size < PAGE_SIZE
          } else {
            lastDocument.value = null
            isLastPage.value = true
          }
        } catch (error) {
          console.error('Error processing snapshot:', error)
        } finally {
          loading.value = false
        }
      },
      (error) => {
        console.error('Error in snapshot listener:', error)
        loading.value = false
        // If error is due to missing index, show helpful message
        if (error.message && error.message.includes('index')) {
          console.warn('Firestore index may be required. Check the error message for index creation link.')
        }
      }
    )
  } catch (error) {
    console.error('Error setting up snapshot listener:', error)
    loading.value = false
  }
}

/**
 * Load initial transactions on mount or when filters change
 */
const loadInitialTransactions = async () => {
  setupSnapshotListener()
}


/**
 * Load more transactions (pagination)
 */
const loadMoreTransactions = async () => {
  if (!lastDocument.value || isLastPage.value || loadingMore.value) {
    return
  }

  loadingMore.value = true
  try {
    const result = await fetchTransactions(lastDocument.value)
    transactions.value = [...transactions.value, ...result.transactions]
    lastDocument.value = result.lastDoc
    isLastPage.value = result.isLastPage
  } catch (error) {
    console.error('Error loading more transactions:', error)
  } finally {
    loadingMore.value = false
  }
}

/**
 * Format currency amount
 * @param {number} amount - Amount to format
 * @returns {string} Formatted currency string
 */
const formatCurrency = (amount) => {
  if (amount == null) return '0.00'
  return new Intl.NumberFormat('en-PH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount)
}

/**
 * Format date from Firestore timestamp
 * @param {Object|Date|string} timestamp - Firestore timestamp or date
 * @returns {string} Formatted date string
 */
const formatDate = (timestamp) => {
  if (!timestamp) return 'N/A'
  
  let date
  if (timestamp?.toDate) {
    // Firestore Timestamp
    date = timestamp.toDate()
  } else if (timestamp instanceof Date) {
    date = timestamp
  } else if (typeof timestamp === 'string' || typeof timestamp === 'number') {
    date = new Date(timestamp)
  } else {
    return 'N/A'
  }

  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date)
}

/**
 * Open raw data modal with transaction data
 * @param {Object} transaction - Transaction object
 */
const openRawModal = (transaction) => {
  try {
    // Format the entire transaction object as JSON
    rawTransactionData.value = JSON.stringify(transaction, null, 2)
    showRawModal.value = true
  } catch (error) {
    console.error('Error formatting transaction data:', error)
    rawTransactionData.value = 'Error formatting transaction data'
    showRawModal.value = true
  }
}

/**
 * Close raw data modal
 */
const closeRawModal = () => {
  showRawModal.value = false
  rawTransactionData.value = ''
}

/**
 * Open notes modal with transaction data
 * @param {Object} transaction - Transaction object
 */
const openNotesModal = (transaction) => {
  currentTransaction.value = transaction
  notesText.value = transaction.data.notes?.data || ''
  showNotesModal.value = true
}

/**
 * Close notes modal
 */
const closeNotesModal = () => {
  showNotesModal.value = false
  notesText.value = ''
  currentTransaction.value = null
}

/**
 * Save notes to Firestore
 */
const saveNotes = async () => {
  if (!currentTransaction.value || !auth.currentUser) {
    return
  }

  savingNotes.value = true
  try {
    const transactionRef = doc(db, 'transactions', currentTransaction.value.id)
    const trimmedNotes = notesText.value.trim()

    let updateData
    if (trimmedNotes) {
      // If notes exist, save with the specified structure
      updateData = {
        notes: {
          data: trimmedNotes,
          lastUpdated: Timestamp.now(),
          lastUpdateBy: auth.currentUser.email || 'unknown'
        }
      }
    } else {
      // If notes are empty, delete the notes field
      updateData = {
        notes: deleteField()
      }
    }

    await updateDoc(transactionRef, updateData)

    // Update local transaction data to reflect changes
    if (trimmedNotes) {
      currentTransaction.value.data.notes = updateData.notes
      // Also update in the transactions array
      const transactionIndex = transactions.value.findIndex(t => t.id === currentTransaction.value.id)
      if (transactionIndex !== -1) {
        transactions.value[transactionIndex].data.notes = updateData.notes
      }
    } else {
      // Remove notes from local data
      if (currentTransaction.value.data.notes) {
        delete currentTransaction.value.data.notes
      }
      const transactionIndex = transactions.value.findIndex(t => t.id === currentTransaction.value.id)
      if (transactionIndex !== -1 && transactions.value[transactionIndex].data.notes) {
        delete transactions.value[transactionIndex].data.notes
      }
    }

    closeNotesModal()
  } catch (error) {
    console.error('Error saving notes:', error)
    alert('Failed to save notes. Please try again.')
  } finally {
    savingNotes.value = false
  }
}

// Load transactions on mount
onMounted(async () => {
  await fetchAllSchools() // Fetch schools first
  loadInitialTransactions()
})

// Clean up snapshot listener on unmount
onBeforeUnmount(() => {
  if (unsubscribeSnapshot) {
    unsubscribeSnapshot()
    unsubscribeSnapshot = null
  }
})
</script>

<style scoped>
/* Minimal dark table styles */
table {
  border-collapse: collapse;
}

/* Remove dropdown arrow from select */
select.no-arrow {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: none;
}

select.no-arrow::-ms-expand {
  display: none;
}
</style>